/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import OverView from './OverVIew';
import { getAllCustomer, getAllDocumentCategory, handleScroll, getAllUsers } from '../../../utils';
import { DASHBOARD_COLUMNS, ROLES, ROUTES, showAddClientButton } from '../../../constants';
import { QUERY_KEYS } from '../../../constants/queryKey';
import Card from './Card';
import { AddNewCustomer, DocumentCategoryAddEdit } from '../../shared';
import { getComplianceStatusCards, getComplianceThresholds } from '../../../utils/apiServices/complianceApi';

const offset = 1,
    limit = 5;

export const DashboardContent = () => {
    const navigate = useNavigate();
    const userRole = useSelector((s) => s.user?.role);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isDocCategoryModelOpen, setIsDocCategoryModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const scrollBar = useRef(null);

    const adminQuery = useQuery({
        queryKey: [QUERY_KEYS.ADMIN_USER, '5'],
        queryFn: () => getAllUsers({ offset, limit, role: [ROLES.ADMIN, ROLES.SUPER_ADMIN] }),
        select: (data) => data?.data || [],
    });

    const csmQuery = useQuery({
        queryKey: [QUERY_KEYS.CSM_USER, '5'],
        queryFn: () => getAllUsers({ offset, limit, role: [ROLES.CSM] }),
        select: (data) => data?.data || [],
    });

    const documentCategoryQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENT_CATEGORY, offset, limit],
        queryFn: () => getAllDocumentCategory({ offset, limit }),
        enabled: ![ROLES.CONTENT_MANAGER, ROLES.SALES_AGENT].includes(userRole),
        select: (data) => data?.data || [],
    });

    const customerQuery = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS],
        queryFn: () => getAllCustomer({ offset, limit }),
        select: (data) => data?.data || [],
    });

    const complianceQuery = useQuery({
        queryKey: [QUERY_KEYS.COMPLIANCE_STATUS, offset, limit],
        queryFn: () => getComplianceStatusCards({ limit, offset }),
        select: (data) => ({ enabled: !!data?.data?.enabled, cards: (data?.data?.cards || []).slice(0, 5) }),
    });

    const thresholdsQuery = useQuery({
        queryKey: [QUERY_KEYS.COMPLIANCE_STATUS, 'thresholds'],
        queryFn: () => getComplianceThresholds(),
        select: (data) => data?.data?.defaults || { greenDays: 30, yellowDays: 14 },
    });

    const handleScrolling = () => {
        handleScroll(setIsScrolling, scrollBar, 2000);
    };

    return (
        <div
            onScroll={handleScrolling}
            onMouseMove={handleScrolling}
            className={`dashbaord-view-scroll pe-2 ${isScrolling ? 'scrolling' : 'not-scrolling'}`}
        >
            <OverView />
            <div className='dashboard-main-content mt-lg-4 mt-3 pb-5'>
                <div className='row'>
                    <Card
                        heading={DASHBOARD_COLUMNS.ADMIN}
                        data={adminQuery.data}
                        view={ROUTES.ADMIN.ADMIN}
                        onClickNext={(record) => {
                            navigate(ROUTES.ADMIN.EDIT_PROFILE, {
                                state: { userId: record.id, adminData: true },
                            });
                        }}
                        isLoading={adminQuery.isFetching}
                    />
                    <Card
                        heading={DASHBOARD_COLUMNS.CSM}
                        data={csmQuery.data}
                        view={ROUTES.ADMIN.CSM}
                        onClickNext={(record) => {
                            navigate(ROUTES.ADMIN.EDIT_PROFILE, { state: { userId: record.id } });
                        }}
                        isLoading={csmQuery.isFetching}
                    />
                    <Card
                        heading={DASHBOARD_COLUMNS.CUSTOMER}
                        data={customerQuery.data}
                        view={ROUTES.ADMIN.CUSTOMER}
                        showAddButton={showAddClientButton(userRole)}
                        onClickAdd={() => setIsCustomerModalOpen(true)}
                        onClickNext={(record) => {
                            navigate(ROUTES.ADMIN.EDIT_PROFILE, {
                                state: { customerId: record.id, customerProfile: true },
                            });
                        }}
                        isLoading={customerQuery.isFetching}
                    />
                    {complianceQuery.data?.enabled && (
                    <Card
                        heading={'Compliance Status'}
                        data={(complianceQuery.data?.cards || []).map((c) => ({ name: `${c.account_name} — ${c.document_name} (${c.status.toUpperCase()})`, email: c.reason }))}
                        view={ROUTES.ADMIN.CUSTOMER}
                        isLoading={complianceQuery.isFetching}
                    />)}
                    {complianceQuery.data?.enabled && !complianceQuery.isFetching && (
                        <div className='col-12'><small className='text-muted'>Thresholds: Green ≥ {thresholdsQuery.data?.greenDays ?? 30} days, Yellow ≥ {thresholdsQuery.data?.yellowDays ?? 14} days</small></div>
                    )}
                    <Card
                        heading={DASHBOARD_COLUMNS.DOCUMENT_CATEGORY}
                        data={documentCategoryQuery.data}
                        view={ROUTES.ADMIN.DOCUMENT_CATEGORY}
                        showAddButton={showAddClientButton(userRole)}
                        onClickAdd={() => setIsDocCategoryModalOpen(true)}
                        onClickNext={(record) => {
                            navigate(ROUTES.ADMIN.DOCUMENT_CATEGORY, { state: { id: record.id } });
                        }}
                        allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CSM]}
                        isLoading={documentCategoryQuery.isFetching}
                    />
                </div>
            </div>
            {isDocCategoryModelOpen && (
                <DocumentCategoryAddEdit
                    openEditModal={isDocCategoryModelOpen}
                    setOpenEditModal={setIsDocCategoryModalOpen}
                    isEdit={false}
                    debouncedApiCall={documentCategoryQuery.refetch}
                />
            )}

            {isCustomerModalOpen && (
                <AddNewCustomer
                    debouncedApiCall={customerQuery.refetch}
                    showAddModal={isCustomerModalOpen}
                    setShowAddModal={setIsCustomerModalOpen}
                />
            )}
        </div>
    );
};
